import { motion } from "framer-motion";
import { Film, Sparkles, Volume2, Palette, Eye } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

function Card({ idx, icon: Icon, title, desc, className, children, testid }) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      custom={idx}
      data-testid={testid}
      className={`sheen-card group relative overflow-hidden rounded-3xl border border-white/8 bg-[#0E0E12] p-8 transition-colors duration-500 hover:border-white/20 ${className}`}
    >
      <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-[#FF3B30]/15 blur-[80px] opacity-0 transition-opacity duration-700 group-hover:opacity-100" />
      <div className="flex items-start justify-between">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-[#16161D] transition-colors duration-500 group-hover:border-[#FF3B30]/40">
          <Icon className="h-5 w-5 text-[#FF9500]" strokeWidth={1.75} />
        </div>
        <span className="mono-label text-[11px] text-zinc-600">/{String(idx + 1).padStart(2, "0")}</span>
      </div>
      <h3 className="mt-8 font-display text-2xl font-bold uppercase tracking-wide sm:text-3xl">{title}</h3>
      <p className="mt-3 max-w-md text-sm leading-relaxed text-zinc-400">{desc}</p>
      {children}
    </motion.div>
  );
}

export default function Services() {
  return (
    <section id="services" className="relative py-24 lg:py-32">
      <div className="wrap">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="mb-14 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between"
        >
          <div>
            <p className="eyebrow mono-label text-[11px] text-[#FF3B30]">What I provide</p>
            <h2 className="mt-5 font-display text-4xl font-black uppercase leading-[0.9] tracking-tight sm:text-5xl lg:text-6xl">
              Every frame,
              <br />
              <span className="text-gradient">engineered.</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-zinc-400">
            Five crafts, one goal — videos your audience can&apos;t skip, swipe past, or forget.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-12" data-testid="services-bento-grid">
          <Card
            idx={0}
            icon={Film}
            title="Video Editing"
            desc="Pacing-driven, high-retention cuts engineered for maximum audience engagement."
            className="md:col-span-2 lg:col-span-7"
            testid="service-card-video-editing"
          >
            <div className="mt-8 flex h-12 items-center gap-1.5" aria-hidden="true">
              {["22%", "14%", "30%", "10%", "24%"].map((w, i) => (
                <div
                  key={i}
                  style={{
                    width: w,
                    background: i % 2 ? "rgba(142,45,226,0.35)" : "rgba(255,59,48,0.35)",
                  }}
                  className="h-full rounded-md border border-white/10 transition-all duration-500 group-hover:border-white/25"
                />
              ))}
            </div>
          </Card>

          <Card
            idx={1}
            icon={Sparkles}
            title="Motion Graphics"
            desc="Custom lower thirds, dynamic title reveals, kinetic typography and asset animation."
            className="lg:col-span-5"
            testid="service-card-motion-graphics"
          />

          <Card
            idx={2}
            icon={Volume2}
            title="Sound Designing"
            desc="Immersive SFX layering, audio leveling and punchy impact sounds that land every cut."
            className="lg:col-span-4"
            testid="service-card-sound-designing"
          />

          <Card
            idx={3}
            icon={Palette}
            title="Colour Grading"
            desc="Cinematic LUTs, skin-tone correction and film print emulations for a signature look."
            className="lg:col-span-4"
            testid="service-card-colour-grading"
          />

          <Card
            idx={4}
            icon={Eye}
            title="Visual Storytelling"
            desc="Narrative structure, hooked openings and emotional arcs tuned for retention."
            className="lg:col-span-4"
            testid="service-card-visual-storytelling"
          />
        </div>
      </div>
    </section>
  );
}
